/**
 * Explore's Settings side panel, two tabs as in AgencyAnalytics: Metrics (a
 * checklist grouped by category, each with Select All / Clear All and a
 * selected count; a toggle applies at once and carries across views) and
 * Settings (the connected property, with Manage).
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

import type { CatalogueMetric } from '../../reports/reports-api.ts';

export function ExploreSettings(props: {
  metrics: CatalogueMetric[];
  enabled: string[];
  onToggle: (key: string) => void;
  onSetMany: (keys: string[], on: boolean) => void;
  property: string | null;
  manageHref: string;
  onClose: () => void;
}): ReactNode {
  const [tab, setTab] = useState<'metrics' | 'settings'>('metrics');
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    panel.current?.focus();
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && props.onClose();
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      opener?.focus();
    };
  }, []);
  const groups = [...new Set(props.metrics.map((metric) => metric.group ?? 'Other'))];

  return (
    <div className="ex-drawer-backdrop" onClick={props.onClose}>
      <div
        className="ex-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Explore settings"
        tabIndex={-1}
        ref={panel}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="ex-drawer-head">
          <div className="ex-switch" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'metrics'}
              onClick={() => setTab('metrics')}
            >
              Metrics
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'settings'}
              onClick={() => setTab('settings')}
            >
              Settings
            </button>
          </div>
          <button
            type="button"
            className="ex-close"
            aria-label="Close settings"
            onClick={props.onClose}
          >
            ×
          </button>
        </div>
        {tab === 'metrics' &&
          groups.map((group) => {
            const inGroup = props.metrics.filter((metric) => (metric.group ?? 'Other') === group);
            const count = inGroup.filter((metric) => props.enabled.includes(metric.key)).length;
            const keys = inGroup.map((metric) => metric.key);
            return (
              <fieldset key={group} className="ex-group">
                <legend>
                  {group} <span className="ex-count">{count} selected</span>
                </legend>
                <div className="ex-group-actions">
                  <button
                    type="button"
                    className="ex-link"
                    onClick={() => props.onSetMany(keys, true)}
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    className="ex-link"
                    onClick={() => props.onSetMany(keys, false)}
                  >
                    Clear All
                  </button>
                </div>
                {inGroup.map((metric) => (
                  <label key={metric.key}>
                    <input
                      type="checkbox"
                      checked={props.enabled.includes(metric.key)}
                      onChange={() => props.onToggle(metric.key)}
                    />
                    {metric.label}
                  </label>
                ))}
              </fieldset>
            );
          })}
        {tab === 'settings' && (
          <div className="ex-group">
            <h3>Connected property</h3>
            <p>{props.property ?? 'No GA4 property is attached to this client.'}</p>
            <a className="btn secondary" href={props.manageHref}>
              Manage
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
